import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dices, RotateCcw, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import type { AlgorithmMeta } from '../../../core/registry/algorithmRegistry';
import {
  getInputSchema,
  randomizeValues,
  type FieldSchema,
  type InputSchema,
} from '../../../core/utils/inputSchemas';
import { computeInputSizeWarning } from '../../../core/utils/frameClamp';

// ── types ────────────────────────────────────────────────────────────────────

export interface AlgorithmInputEditorProps {
  algorithm: AlgorithmMeta;
  onInputReady: (envelope: unknown) => void;
}

// ── field components ─────────────────────────────────────────────────────────

interface FieldProps {
  field: FieldSchema;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  error?: string;
}

function NumberArrayField({ field, value, onChange, error }: FieldProps) {
  const raw = Array.isArray(value) ? (value as number[]).join(', ') : String(value ?? '');
  const [text, setText] = useState(raw);

  useEffect(() => {
    const next = Array.isArray(value) ? (value as number[]).join(', ') : String(value ?? '');
    setText(next);
  }, [value]);

  const commit = useCallback(
    (s: string) => {
      const parsed = s
        .split(/[\s,]+/)
        .filter(Boolean)
        .map(Number)
        .filter((n) => !isNaN(n));
      onChange(field.key, parsed);
    },
    [field.key, onChange],
  );

  return (
    <input
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      placeholder={field.description ?? '1, 2, 3, …'}
      className={fieldCls(!!error)}
      spellCheck={false}
    />
  );
}

function NumberField({ field, value, onChange, error }: FieldProps) {
  return (
    <input
      type="number"
      value={value as number}
      min={field.min}
      max={field.max}
      onChange={(e) => onChange(field.key, Number(e.target.value))}
      className={fieldCls(!!error)}
    />
  );
}

function StringField({ field, value, onChange, error }: FieldProps) {
  return (
    <input
      value={String(value ?? '')}
      onChange={(e) => onChange(field.key, e.target.value)}
      placeholder={field.description}
      className={fieldCls(!!error)}
      spellCheck={false}
    />
  );
}

function WordListField({ field, value, onChange, error }: FieldProps) {
  const raw = Array.isArray(value) ? (value as string[]).join(', ') : String(value ?? '');
  const [text, setText] = useState(raw);

  useEffect(() => {
    const next = Array.isArray(value) ? (value as string[]).join(', ') : String(value ?? '');
    setText(next);
  }, [value]);

  const commit = useCallback(
    (s: string) => {
      const words = s.split(/[\s,]+/).filter(Boolean);
      onChange(field.key, words);
    },
    [field.key, onChange],
  );

  return (
    <input
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      placeholder={field.description ?? 'word1, word2, …'}
      className={fieldCls(!!error)}
      spellCheck={false}
    />
  );
}

function JsonField({ field, value, onChange, error }: FieldProps) {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [localErr, setLocalErr] = useState<string | null>(null);

  useEffect(() => {
    setText(JSON.stringify(value, null, 2));
    setLocalErr(null);
  }, [value]);

  const commit = useCallback(
    (s: string) => {
      try {
        const parsed = JSON.parse(s);
        setLocalErr(null);
        onChange(field.key, parsed);
      } catch {
        setLocalErr('Invalid JSON');
      }
    },
    [field.key, onChange],
  );

  return (
    <>
      <textarea
        value={text}
        rows={4}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        placeholder={field.description ?? '{}'}
        className={`${fieldCls(!!(error || localErr))} font-mono text-[11px] resize-none`}
        spellCheck={false}
      />
      {localErr && <p className="mt-0.5 text-[10px] text-red-400">{localErr}</p>}
    </>
  );
}

function MatrixField({ field, value, onChange, error }: FieldProps) {
  const [text, setText] = useState(JSON.stringify(value));
  const [localErr, setLocalErr] = useState<string | null>(null);

  useEffect(() => {
    setText(JSON.stringify(value));
    setLocalErr(null);
  }, [value]);

  const commit = useCallback(
    (s: string) => {
      try {
        const parsed = JSON.parse(s);
        if (!Array.isArray(parsed)) throw new Error('not an array');
        setLocalErr(null);
        onChange(field.key, parsed);
      } catch {
        setLocalErr('Must be a 2D JSON array');
      }
    },
    [field.key, onChange],
  );

  return (
    <>
      <textarea
        value={text}
        rows={5}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        placeholder="[[1,0],[0,1]]"
        className={`${fieldCls(!!(error || localErr))} font-mono text-[11px] resize-none`}
        spellCheck={false}
      />
      {localErr && <p className="mt-0.5 text-[10px] text-red-400">{localErr}</p>}
    </>
  );
}

function EdgeListField({ field, value, onChange, error }: FieldProps) {
  const edges = Array.isArray(value) ? value : [];
  const [text, setText] = useState(JSON.stringify(edges, null, 2));
  const [localErr, setLocalErr] = useState<string | null>(null);

  useEffect(() => {
    setText(JSON.stringify(Array.isArray(value) ? value : [], null, 2));
    setLocalErr(null);
  }, [value]);

  const commit = useCallback(
    (s: string) => {
      try {
        const parsed = JSON.parse(s);
        if (!Array.isArray(parsed)) throw new Error();
        setLocalErr(null);
        onChange(field.key, parsed);
      } catch {
        setLocalErr('Must be a JSON array of edges');
      }
    },
    [field.key, onChange],
  );

  return (
    <>
      <textarea
        value={text}
        rows={5}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        placeholder='[{"from":"A","to":"B","weight":1}]'
        className={`${fieldCls(!!(error || localErr))} font-mono text-[11px] resize-none`}
        spellCheck={false}
      />
      {localErr && <p className="mt-0.5 text-[10px] text-red-400">{localErr}</p>}
    </>
  );
}

// ── shared style helper ──────────────────────────────────────────────────────

function fieldCls(hasError: boolean) {
  return [
    'w-full px-2.5 py-1.5 rounded-md text-[12px] font-mono text-zinc-200 outline-none',
    'bg-zinc-900/60 border transition-colors duration-100',
    hasError
      ? 'border-red-500/50 focus:border-red-400'
      : 'border-white/10 focus:border-indigo-500/50',
  ].join(' ');
}

// ── field dispatcher ─────────────────────────────────────────────────────────

function Field(props: FieldProps) {
  switch (props.field.type) {
    case 'number-array': return <NumberArrayField {...props} />;
    case 'number':       return <NumberField {...props} />;
    case 'string':       return <StringField {...props} />;
    case 'string-pair':  return <StringField {...props} />;
    case 'word-list':    return <WordListField {...props} />;
    case 'matrix':       return <MatrixField {...props} />;
    case 'edge-list':    return <EdgeListField {...props} />;
    case 'json':         return <JsonField {...props} />;
    default:             return <StringField {...props} />;
  }
}

// ── validation ────────────────────────────────────────────────────────────────

function validateValues(
  schema: InputSchema,
  values: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of schema.fields) {
    const v = values[field.key];
    if (field.type === 'number-array') {
      if (!Array.isArray(v) || (v as unknown[]).length === 0) {
        errors[field.key] = 'Must be a non-empty array of numbers';
      }
    } else if (field.type === 'number') {
      const n = Number(v);
      if (isNaN(n)) errors[field.key] = 'Must be a number';
      else if (field.min !== undefined && n < field.min) errors[field.key] = `Min: ${field.min}`;
      else if (field.max !== undefined && n > field.max) errors[field.key] = `Max: ${field.max}`;
    } else if (field.type === 'word-list') {
      if (!Array.isArray(v) || (v as unknown[]).length === 0)
        errors[field.key] = 'Must be at least one word';
    }
  }
  return errors;
}

// ── preview ───────────────────────────────────────────────────────────────────

interface PreviewProps {
  envelope: unknown;
  open: boolean;
  onToggle: () => void;
}

function InputPreview({ envelope, open, onToggle }: PreviewProps) {
  const text = useMemo(() => {
    try {
      return JSON.stringify(envelope, null, 2);
    } catch {
      return '(cannot serialize)';
    }
  }, [envelope]);

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-400 transition-colors"
        style={{ background: 'rgba(255,255,255,0.02)' }}
      >
        <span>Input preview</span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <pre
          className="px-3 py-2 text-[10px] font-mono text-zinc-400 overflow-x-auto max-h-40 overflow-y-auto"
          style={{ background: 'rgba(0,0,0,0.25)' }}
        >
          {text}
        </pre>
      )}
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export const AlgorithmInputEditor: React.FC<AlgorithmInputEditorProps> = ({
  algorithm,
  onInputReady,
}) => {
  const schema = useMemo(
    () => getInputSchema(algorithm.id, algorithm.category),
    [algorithm.id, algorithm.category],
  );

  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...schema.defaults }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewOpen, setPreviewOpen] = useState(false);

  // Reset values when algorithm changes
  const prevIdRef = useRef(algorithm.id);
  useEffect(() => {
    if (prevIdRef.current !== algorithm.id) {
      prevIdRef.current = algorithm.id;
      setValues({ ...schema.defaults });
      setErrors({});
    }
  }, [algorithm.id, schema.defaults]);

  const envelope = useMemo(() => schema.toInputEnvelope(values), [schema, values]);

  const inputSizeWarning = useMemo(() => {
    for (const field of schema.fields) {
      if (field.type === 'number-array' || field.type === 'word-list' || field.type === 'edge-list') {
        const v = values[field.key];
        if (Array.isArray(v)) {
          const w = computeInputSizeWarning(algorithm.category, (v as unknown[]).length);
          if (w) return w;
        }
      }
    }
    return null;
  }, [schema.fields, values, algorithm.category]);

  const handleChange = useCallback((key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleRun = useCallback(() => {
    const errs = validateValues(schema, values);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onInputReady(schema.toInputEnvelope(values));
  }, [onInputReady, schema, values]);

  const handleRandomize = useCallback(() => {
    const rand = randomizeValues(schema);
    setValues(rand);
    setErrors({});
    onInputReady(schema.toInputEnvelope(rand));
  }, [schema, onInputReady]);

  const handleReset = useCallback(() => {
    setValues({ ...schema.defaults });
    setErrors({});
  }, [schema.defaults]);

  return (
    <div className="flex flex-col gap-3 p-3 overflow-y-auto">
      {/* Max input size warning */}
      {inputSizeWarning && (
        <div
          className="flex items-start gap-2 rounded-lg px-3 py-2 text-[11px] leading-snug"
          role="alert"
          style={{
            background: 'rgba(251,191,36,0.08)',
            border: '1px solid rgba(251,191,36,0.28)',
            color: '#fbbf24',
          }}
        >
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden="true" />
          <span>{inputSizeWarning}</span>
        </div>
      )}
      {/* Fields */}
      <div className="flex flex-col gap-3">
        {schema.fields.map((field) => (
          <div key={field.key} className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              {field.label}
              {field.description && (
                <span className="ml-1.5 normal-case text-zinc-600 font-normal">
                  — {field.description}
                </span>
              )}
            </label>
            <Field
              field={field}
              value={values[field.key]}
              onChange={handleChange}
              error={errors[field.key]}
            />
            {errors[field.key] && (
              <p className="text-[10px] text-red-400">{errors[field.key]}</p>
            )}
          </div>
        ))}
      </div>

      {/* Action row */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleRun}
          className="flex-1 py-1.5 rounded-md text-[11px] font-semibold transition-colors"
          style={{
            background: 'rgba(99,102,241,0.22)',
            border: '1px solid rgba(99,102,241,0.35)',
            color: '#c7d2fe',
          }}
        >
          Run with this input
        </button>
        <button
          onClick={handleRandomize}
          title="Randomize"
          className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 transition-colors"
          style={{ border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <Dices className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleReset}
          title="Reset to default"
          className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 transition-colors"
          style={{ border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Preview */}
      <InputPreview
        envelope={envelope}
        open={previewOpen}
        onToggle={() => setPreviewOpen((o) => !o)}
      />
    </div>
  );
};

AlgorithmInputEditor.displayName = 'AlgorithmInputEditor';
